
export type SongSection = 'Verse' | 'Chorus' | 'Bridge';

interface SectionConfig {
    minDuration: number;
    maxDuration: number;
    minTimeSeconds: number; // P2: Minimum time in seconds
}

const SECTION_CONFIGS: Record<SongSection, SectionConfig> = {
    'Verse': { minDuration: 12, maxDuration: 32, minTimeSeconds: 15 },
    'Chorus': { minDuration: 16, maxDuration: 32, minTimeSeconds: 12 },
    'Bridge': { minDuration: 8, maxDuration: 16, minTimeSeconds: 8 }
};

/**
 * P0 FIX: Cadence Type Hierarchy
 * - 'none': No cadence (mid-phrase)
 * - 'half': Ends on V (pause, not ending)
 * - 'imperfect': V→I with melody NOT on tonic (partial closure)
 * - 'perfect': V→I with melody on tonic (full closure)
 * - 'plagal': IV→I ("Amen" closure)
 * - 'deceptive': V→vi (surprise twist)
 */
export type CadenceType = 'none' | 'half' | 'imperfect' | 'perfect' | 'plagal' | 'deceptive';

import { TIMBRE_PRESETS } from '../audio/TimbrePresets';

export class StructureEngine {
    private currentSection: SongSection = 'Verse';
    private sectionTouchCount: number = 0;
    private sectionDuration: number = 16;
    private currentSectionConfig: SectionConfig = SECTION_CONFIGS['Verse'];
    private totalStructure: SongSection[] = ['Verse', 'Chorus', 'Verse', 'Bridge', 'Chorus'];
    private structureIndex: number = 0;

    // P2 FIX: Time-Aware Structure
    private sectionStartTime: number = Date.now();

    // P0 FIX: Cache harmonic rhythm per section (no more per-touch randomization)
    private cachedHarmonicRhythm: number = 8;

    // AUDIT FIX: Phrase-Locked Transitions
    // Transitions are queued when conditions are met, but only execute at phrase boundaries
    private pendingTransition: boolean = false;
    private touchesSinceTransitionQueued: number = 0;
    private readonly MAX_PHRASE_WAIT: number = 8; // Safety: don't wait more than 8 touches for phrase end

    constructor() {
        this.reset();
    }

    reset(customStructure?: SongSection[]) {
        if (customStructure) {
            this.totalStructure = [...customStructure];
        } else {
            // Fallback default
            this.totalStructure = ['Verse', 'Chorus', 'Verse', 'Bridge', 'Chorus'];
        }

        this.currentSection = this.totalStructure[0];
        this.currentSectionConfig = SECTION_CONFIGS[this.currentSection];
        this.structureIndex = 0;
        this.sectionTouchCount = 0;
        this.sectionDuration = 16; // Legacy fallback, but we'll use dynamic logic
        this.sectionStartTime = Date.now(); // P2
        this.cachedHarmonicRhythm = this.calculateHarmonicRhythm();

        // AUDIT FIX: Reset phrase-lock state
        this.pendingTransition = false;
        this.touchesSinceTransitionQueued = 0;
    }

    /**
     * P0 FIX: Calculate harmonic rhythm once per section
     */
    private calculateHarmonicRhythm(): number {
        switch (this.currentSection) {
            case 'Verse':
                return Math.floor(Math.random() * 5) + 8; // 8-12
            case 'Chorus':
                return Math.floor(Math.random() * 5) + 4; // 4-8
            case 'Bridge':
                return Math.floor(Math.random() * 3) + 2; // 2-4
            default:
                return 8;
        }
    }

    /**
     * P0 FIX: Ensure cached harmonic rhythm is set (call at section start)
     */
    private ensureCachedHarmonicRhythm(): void {
        this.cachedHarmonicRhythm = this.calculateHarmonicRhythm();
    }

    /**
     * AUDIT FIX: Check if we're at a phrase boundary (start of a new 4-bar phrase)
     * This is the musically appropriate place for section transitions
     */
    private isPhraseEnd(): boolean {
        // We consider "phrase end" as the moment AFTER measure 3, beat 3
        // i.e., when we've just rolled over to measure 0, beat 0
        return this.measureCount === 0 && this.beatCount === 0;
    }

    /**
     * P0 FIX: Get cadence type based on phrase and song position.
     * - Song finale (>95% progress): perfect
     * - Approaching song end (>85%): half (dominant preparation)
     * - Phrase ending (measure 3): half or imperfect
     * - Section boundaries: varies by section type
     */
    getCadenceType(): CadenceType {
        const songProgress = this.getSongProgress();
        const touchesRemainingInSection = this.sectionDuration - this.sectionTouchCount;

        // FINALE: Perfect authentic cadence for the last moments
        if (songProgress > 0.95) {
            return 'perfect';
        }

        // APPROACHING END: Half cadences to build anticipation
        if (songProgress > 0.85) {
            // Alternate between half and imperfect to build tension
            return this.measureCount % 2 === 0 ? 'half' : 'imperfect';
        }

        // SECTION BOUNDARY: Strong cadence when section is about to change
        if (touchesRemainingInSection <= 2) {
            if (this.currentSection === 'Bridge') {
                // Bridge → Chorus should be dramatic
                return Math.random() < 0.3 ? 'deceptive' : 'half';
            }
            return 'imperfect';
        }

        // PHRASE ENDING: Half cadence at bar 4
        if (this.measureCount === 3 && this.beatCount >= 2) {
            return 'half';
        }

        // PHRASE START: Resolution feel at bar 1
        if (this.measureCount === 0 && this.beatCount < 2) {
            // After a phrase, the new phrase feels resolved
            return this.sectionTouchCount > 4 ? 'imperfect' : 'none';
        }

        return 'none';
    }

    /**
     * P0 FIX: Get phrase position within current section
     * Returns normalized position (0-1) and whether we're approaching a cadence
     */
    getPhrasePositionInSection(): { position: number; measureInPhrase: number; beatInMeasure: number } {
        const position = this.sectionTouchCount / this.sectionDuration;
        return {
            position,
            measureInPhrase: this.measureCount,
            beatInMeasure: this.beatCount
        };
    }

    /**
     * P0 FIX: Check if we're approaching a cadence (phrase or song ending)
     */
    isApproachingCadence(): boolean {
        const songProgress = this.getSongProgress();
        const touchesRemainingInSection = this.sectionDuration - this.sectionTouchCount;

        // Approaching song end
        if (songProgress > 0.85) return true;

        // Approaching section end
        if (touchesRemainingInSection <= 4) return true;

        // Approaching phrase end (bar 3-4)
        if (this.measureCount >= 3) return true;

        return false;
    }

    /**
     * P0 FIX: Get ritardando (slowdown) factor for song ending
     * Returns 1.0 for normal tempo, < 1.0 for slower (e.g., 0.85 = 15% slower)
     * Only applies in the final 8 touches of the song
     */
    getRitardandoFactor(): number {
        const touchesRemaining = this.getTouchesRemaining();

        if (touchesRemaining > 8) return 1.0;

        // Gradual slowdown: 1.0 → 0.7 over last 8 touches
        // Touch 8: 1.0, Touch 1: 0.7
        const slowdownRange = 0.3; // 30% slowdown at minimum
        const progress = (8 - touchesRemaining) / 8;
        return 1.0 - (progress * slowdownRange);
    }

    /**
     * P3 FIX: Check if we're in the final Chorus for obligatory hooks
     * Returns true if current section is Chorus and it's the last Chorus in the structure
     */
    isInFinalChorus(): boolean {
        if (this.currentSection !== 'Chorus') return false;

        // Check if there are any more Chorus sections after current position
        for (let i = this.structureIndex + 1; i < this.totalStructure.length; i++) {
            if (this.totalStructure[i] === 'Chorus') {
                return false;  // There's another Chorus later
            }
        }
        return true;  // This is the last Chorus
    }

    getCurrentSection(): SongSection {
        return this.currentSection;
    }

    private measureCount: number = 0; // 0-3 (4 bar phrases)
    private beatCount: number = 0; // 0-3 (4 beats per bar)

    /**
     * Advance the structural state by one touch.
     * Returns a Modulation Event string if a key change is needed, or null.
     */
    advanceTouch(intensity?: number): 'ToRelativeMinor' | 'LiftWholeStep' | 'Reset' | null {
        this.sectionTouchCount++;

        // Phrase / Beat Logic (Approximate 1 touch = 1 beat for now)
        this.beatCount++;
        if (this.beatCount >= 4) {
            this.beatCount = 0;
            this.measureCount = (this.measureCount + 1) % 4;
        }

        // PHASE 2: ADAPTIVE PACING (Elastic Sections)
        let shouldQueueTransition = false;

        // 1. Hard enforce Max Duration
        if (this.sectionTouchCount >= this.currentSectionConfig.maxDuration) {
            shouldQueueTransition = true;
        }
        // 2. Check Elastic Window (Min < Count < Max)
        else if (this.sectionTouchCount >= this.currentSectionConfig.minDuration && intensity !== undefined) {
            let transitionProb = 0;

            if (this.currentSection === 'Verse') {
                // Verse: Build energy usually leads to Chorus. 
                // Probabilistic: Higher intensity = Higher chance to advance.
                // range 0.5 - 1.0 => prob 0 - 0.3 per touch
                transitionProb = Math.max(0, (intensity - 0.5) * 0.6);
            } else if (this.currentSection === 'Chorus') {
                // Chorus: Sustain energy. Drop in intensity = End of chorus.
                // Probabilistic: Lower intensity = Higher chance to end.
                // range 0.5 - 0.0 => prob 0 - 0.3 per touch
                transitionProb = Math.max(0, (0.5 - intensity) * 0.6);
            } else if (this.currentSection === 'Bridge') {
                // Bridge: Needs high intensity to break through or low to fizzle out?
                // Usually Bridge builds to climax (Chorus).
                // High intensity = Pushing to resolve.
                transitionProb = Math.max(0, (intensity - 0.6) * 0.8);
            }

            if (Math.random() < transitionProb) {
                shouldQueueTransition = true;
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[StructureEngine] Adaptive Transition Queued! (Int: ${intensity.toFixed(2)}, Prob: ${transitionProb.toFixed(2)})`);
                }
            }
        }
        // 3. Fallback to Legacy (Fixed 16) if no intensity provided or logic falls through
        else if (intensity === undefined && this.sectionTouchCount >= 16) {
            shouldQueueTransition = true;
        }

        // P2 FIX: Time-Aware Structure Check
        // If we haven't met the minimum time requirement, BLOCK the transition
        // (Unless we hit a hard fail-safe limit like 50 touches to prevent infinite loops)
        const elapsedSeconds = (Date.now() - this.sectionStartTime) / 1000;
        if (shouldQueueTransition && elapsedSeconds < this.currentSectionConfig.minTimeSeconds && this.sectionTouchCount < 64) {
            if (process.env.NODE_ENV === 'development') {
                // console.log(`[StructureEngine] Holding Section: ${elapsedSeconds.toFixed(1)}s < ${this.currentSectionConfig.minTimeSeconds}s`);
            }
            shouldQueueTransition = false;
        }

        // AUDIT FIX: Phrase-Locked Transitions
        // Queue the transition if conditions are met, but wait for phrase boundary
        if (shouldQueueTransition && !this.pendingTransition) {
            this.pendingTransition = true;
            this.touchesSinceTransitionQueued = 0;
            if (process.env.NODE_ENV === 'development') {
                console.log(`[StructureEngine] Transition QUEUED, waiting for phrase boundary...`);
            }
        }

        // Track how long we've been waiting if transition is pending
        if (this.pendingTransition) {
            this.touchesSinceTransitionQueued++;
        }

        // Determine if we should actually execute the transition now
        // Execute if: pending AND (at phrase boundary OR waited too long)
        const shouldExecuteTransition = this.pendingTransition &&
            (this.isPhraseEnd() || this.touchesSinceTransitionQueued > this.MAX_PHRASE_WAIT);

        if (shouldExecuteTransition) {
            // Clear pending state
            this.pendingTransition = false;
            this.touchesSinceTransitionQueued = 0;

            this.structureIndex = (this.structureIndex + 1) % this.totalStructure.length;
            const previousSection: SongSection = this.currentSection;
            this.currentSection = this.totalStructure[this.structureIndex];
            this.currentSectionConfig = SECTION_CONFIGS[this.currentSection]; // Update config
            this.sectionTouchCount = 0;
            // Reset phrase on section change
            this.measureCount = 0;
            this.beatCount = 0;

            // P2 FIX: Update timestamp
            this.sectionStartTime = Date.now();

            // P0 FIX: Recalculate harmonic rhythm for new section
            this.ensureCachedHarmonicRhythm();

            if (process.env.NODE_ENV === 'development') console.log(`[StructureEngine] Section Change: ${this.currentSection}`);

            // SMART MODULATION LOGIC
            // 1. Entering Bridge -> Relative Minor (add tension)
            if (this.currentSection === 'Bridge') {
                return 'ToRelativeMinor';
            }

            // 2. Entering Final Chorus -> The "Truck Driver's" Gear Change (+2 semitones)
            // Dynamic check: Is this the last Chorus in the generated structure?
            if (this.isInFinalChorus()) {
                return 'LiftWholeStep';
            }

            // 3. Reset to original key if loop restarts or entering Verse 1
            if (this.structureIndex === 0) {
                return 'Reset';
            }

            // If returning to a normal section from Bridge, we might want to reset or keep relative?
            // Usually Bridge (Relative Minor) -> Chorus (Original Major) resolves nicely if we just "Reset" 
            // BUT if we want to be fancy, we handle it in ComposerEngine.
            // For now, let's say exiting Bridge goes back to 'Reset' (Original Key).
            if (previousSection === 'Bridge') {
                return 'Reset';
            }
        }
        return null; // No change
    }

    /**
     * PHRASE AWARENESS: Returns the harmonic function for the current moment.
     */
    getPhraseFunction(): 'Tension' | 'Resolution' | 'Neutral' {
        // Bridge is ALWAYS high tension (Resolution delayed until Chorus)
        if (this.currentSection === 'Bridge') return 'Tension';

        // Last bar of phrase (Bar 4) -> Tension
        if (this.measureCount === 3) return 'Tension';
        // First bar of phrase (Bar 0) -> Resolution
        if (this.measureCount === 0) return 'Resolution';
        return 'Neutral';
    }

    /**
     * V9 TENSION/RELEASE: Detect if we're in the "build-up" or "release" zone.
     * Last 4 touches before section change = Tension Phase (building climax)
     * First 2 touches of new section = Release Phase (satisfying resolution)
     */
    getTensionPhase(): 'tension' | 'release' | 'neutral' {
        const touchesRemaining = this.sectionDuration - this.sectionTouchCount;

        // Last 4 touches before section ends = TENSION
        if (touchesRemaining <= 4 && touchesRemaining > 0) {
            return 'tension';
        }

        // First 2 touches of new section = RELEASE  
        if (this.sectionTouchCount <= 2) {
            return 'release';
        }

        return 'neutral';
    }

    /**
     * Get dynamic multipliers based on the current section AND phrase position.
     * Chorus is louder, Verse is moderate, Bridge is dynamic.
     * Adds "Phrase Arc" (Swelling) to make it breathe.
     */
    getSectionDynamics(): number {
        let baseMult = 1.0;
        switch (this.currentSection) {
            case 'Chorus': baseMult = 1.3; break; // Anthemic
            case 'Bridge': baseMult = 1.1; break; // Urgent
            case 'Verse': default: baseMult = 0.8; break; // Intimate
        }

        // PHRASE ARC (Feature 8: Dynamic Intensity)
        let arc = 1.0;
        if (this.measureCount === 0) arc = 0.9;
        if (this.measureCount === 1) arc = 1.0;
        if (this.measureCount === 2) arc = 1.1;
        if (this.measureCount === 3) arc = 0.95;

        // Micro-arc
        const beatArcs = [1.05, 0.95, 1.0, 0.9];
        const beatArc = beatArcs[this.beatCount] || 1.0;

        return baseMult * arc * beatArc;
    }

    /**
     * ORCHESTRATION: Get timbre settings for the synth.
     * V9: Now modulates based on tension phase.
     */
    getOrchestrationSettings() {
        const tensionPhase = this.getTensionPhase();

        // Map Presets to Legacy Keys (filterCutoff, releaseTime, reverbMix)
        let preset = TIMBRE_PRESETS.INTIMATE;

        switch (this.currentSection) {
            case 'Chorus':
                preset = TIMBRE_PRESETS.ANTHEMIC;
                break;
            case 'Bridge':
                preset = TIMBRE_PRESETS.URGENT;
                break;
            case 'Verse':
            default:
                preset = TIMBRE_PRESETS.INTIMATE;
                break;
        }

        let settings = {
            filterCutoff: preset.filter,
            releaseTime: preset.release,
            reverbMix: preset.reverb
        };

        // V9 TENSION/RELEASE MODIFIERS
        if (tensionPhase === 'tension') {
            // Tension: Brighter, Drier, Shorter decay (more urgent)
            settings.filterCutoff = Math.min(1.0, settings.filterCutoff * 1.25);
            settings.reverbMix *= 0.75;
            settings.releaseTime *= 0.8;
        } else if (tensionPhase === 'release') {
            // Release: Warmer, Wetter, Longer decay (satisfying resolution)
            settings.filterCutoff *= 0.85;
            settings.reverbMix = Math.min(1.0, settings.reverbMix * 1.3);
            settings.releaseTime *= 1.2;
        }

        return settings;
    }

    /**
     * V10: INTELLIGENT REGISTER SHIFTING
     * Returns octave offsets for melody, chords, and bass per section.
     * Creates sonic variety across song structure.
     */
    getRegisterSettings(): { melodyOctave: number; chordOctave: number; bassOctave: number; spread: number } {
        switch (this.currentSection) {
            case 'Verse':
                // Intimate, mid-range, compact
                return { melodyOctave: 0, chordOctave: 0, bassOctave: 0, spread: 1 };
            case 'Chorus':
                // Expansive: melody up, bass down, wide spread
                return { melodyOctave: 1, chordOctave: 0, bassOctave: -1, spread: 2 };
            case 'Bridge':
                // Ethereal: everything lifted, tight spread
                return { melodyOctave: 1, chordOctave: 1, bassOctave: 0, spread: 1 };
            default:
                return { melodyOctave: 0, chordOctave: 0, bassOctave: 0, spread: 1 };
        }
    }

    /**
     * V10: RHYTHMIC BREATHING (Adaptive Density)
     * Returns density hint based on phrase position.
     * Bar 1 = sparse, Bar 3-4 = dense.
     */
    getPhraseDensity(): 'sparse' | 'normal' | 'dense' {
        // Bar 1 (measureCount 0): Sparse - room to breathe
        if (this.measureCount === 0) return 'sparse';
        // Bar 4 (measureCount 3): Dense - peak activity before resolution
        if (this.measureCount === 3) return 'dense';
        // Bar 2-3: Normal
        return 'normal';
    }

    /**
     * Returns the overall progress of the song structure (0.0 to 1.0).
     * Used for UI visualization.
     */
    getSongProgress(): number {
        const totalSections = this.totalStructure.length;
        const totalDuration = totalSections * this.sectionDuration;

        const currentProgress = (this.structureIndex * this.sectionDuration) + this.sectionTouchCount;

        return Math.min(1.0, currentProgress / totalDuration);
    }

    /**
     * Helper for Resolution Lookahead: Exact number of touches left in song.
     */
    getTouchesRemaining(): number {
        const totalSections = this.totalStructure.length;
        const totalDuration = totalSections * this.sectionDuration;
        const currentProgress = (this.structureIndex * this.sectionDuration) + this.sectionTouchCount;

        return Math.max(0, totalDuration - currentProgress);
    }

    /**
     * V12.5: PROFESSIONAL FINALE LOGIC
     * < 0.85 = Neutral
     * 0.85 - 0.92 = Preparation (Dominant Pedal)
     * 0.92 - 0.98 = Cadence (Cadential 6/4)
     * > 0.98 = Finale (Tonic Resolution)
     */
    getConclusionState(): 'neutral' | 'preparation' | 'cadence' | 'finale' {
        const progress = this.getSongProgress();
        if (progress > 0.98) return 'finale';
        if (progress > 0.92) return 'cadence';
        if (progress > 0.85) return 'preparation';
        return 'neutral';
    }

    /**
     * HARMONIC RHYTHM
     * P0 FIX: Returns the CACHED target duration (in touches/notes) for chords in this section.
     * This is calculated once at section start for consistency.
     * Verse: Slow (8-12 touches)
     * Chorus: Medium (4-8 touches)
     * Bridge: Fast (2-4 touches)
     */
    getHarmonicRhythmDuration(): number {
        return this.cachedHarmonicRhythm;
    }
}
